import React from "react";
import { ServiceClassificationFields } from "./ServiceClassificationFields";
import FieldHelpTooltip from "../../../../../components/common/FieldHelpTooltip";
import { PartyAddress, ServiceCategory, ServiceCategoryGroup } from "../../../../types/types";
import { ServiceFormValue } from "../serviceFormTypes";
interface DetailsStepProps {
  value: ServiceFormValue;
  partyAddresses: PartyAddress[];
  categories: ServiceCategory[];
  groups?: ServiceCategoryGroup[];
  onChange: (value: ServiceFormValue) => void;
  onCategoryChange: (id: number | null) => void;
}

export function ServiceDetailsStep({
  value,
  partyAddresses,
  categories,
  groups = [],
  onChange,
  onCategoryChange,
}: DetailsStepProps) {
  const update = (changes: Partial<ServiceFormValue>) =>
    onChange({ ...value, ...changes });

  return (
    <>
    <div className="card">
      <div className="card-title">Details</div>
      <div className="form-group">
        <label className="form-label" htmlFor="service-kind">Type</label>
        <select
          id="service-kind"
          className="form-input"
          value={value.serviceKind}
          onChange={(event) =>
            update({ serviceKind: event.target.value as "Service" | "Job" })
          }
        >
          <option value="Service">Service</option>
          <option value="Job">Job</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">
          Name <span className="service-form-required-marker">*</span>
        </label>
        <input
          className="form-input"
          value={value.name}
          onChange={(event) => update({ name: event.target.value })}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-input"
          value={value.description}
          onChange={(event) => update({ description: event.target.value })}
          rows={3}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="service-address">
          Address
          <FieldHelpTooltip text="Select the address where this service will run. If the address is not listed, add it to the organisation first." />
        </label>
        <select
          id="service-address"
          className="form-input"
          value={value.partyAddressId ?? ""}
          onChange={(event) =>
            update({
              partyAddressId: event.target.value
                ? Number(event.target.value)
                : null,
            })
          }
        >
          <option value="">No service address</option>
          {partyAddresses.map(({ partyAddressId, isPrimary, address }) => {
            const label =
              address.fullAddress ||
              [address.addressLine1, address.town, address.postCode]
                .filter(Boolean)
                .join(", ");
            return (
              <option key={partyAddressId} value={partyAddressId}>
                {isPrimary ? "Primary — " : ""}
                {label}
              </option>
            );
          })}
        </select>
      </div>
    </div>
    <div className="card">
      <div className="card-title">Categories</div>
      <ServiceClassificationFields
        value={value}
        categories={categories}
        groups={groups}
        onChange={onChange}
        onCategoryChange={onCategoryChange}
      />
    </div>
    <div className="card">
      <div className="card-title">Communications</div>
      <label className="option-item">
        <input type="checkbox" checked={value.usePrimaryContactForServiceUsers ?? false}
          onChange={event => update({ usePrimaryContactForServiceUsers: event.target.checked })} />
        Contact service users through their primary contact
      </label>
      <p className="field-hint">Campaign rules for this service use each service user's household primary contact. Staff and volunteers are contacted directly. Service users without a primary contact are excluded.</p>
    </div>
    <div className="card">
      <div className="card-title">Community portal</div>
      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            checked={value.isPublishedToPublicPortal ?? false}
            onChange={(event) => update({ isPublishedToPublicPortal: event.target.checked })}
          />{" "}
          Publish to community portal
        </label>
        <p className="muted">Show this service and its eligible upcoming sessions on the community portal. The organisation must also be published.</p>
      </div>
    </div>
    </>
  );
}
